import { useCallback, useEffect, useMemo, type SetStateAction } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  attendeesTypeEnum,
  type AttendeeType,
  type PaymentMethod,
  type StatusType,
} from "../../../../Enums/RegistrationEnum";
import {
  IRegistration,
  IRegistrationGuest,
  MAX_REGISTRATION_GUESTS,
} from "../../../../interfaces/models/IRegistration";
import { parseAdditionalFee } from "../../../../utils/invoiceCalculations";
import {
  applyStaffParticipantPaymentStatus,
  applyStaffPrepaidSelection,
  derivePaymentStatus,
  getPrepaidPaymentAmount,
  hasPrepaidPaymentInLedger,
  normalizeStaffPaymentFields,
  resolveStaffPaymentIncrement,
} from "../utils/participantPaymentRules";
import {
  applyGuestModificationDates,
  createEmptyRegistrationGuest,
  hasVisibleGuestWithMissingName,
  normalizeRegistrationGuests,
  sanitizeGuestsForPersist,
} from "../../../../utils/registrationGuests";
import {
  applySpecialEventGuestModificationDates,
  deriveSpecialEventGuestNames,
  hasSpecialEventGuestsWithMissingNames,
  normalizeSpecialEventGuestsMap,
  sanitizeSpecialEventGuestsForPersist,
  type SpecialEventGuestsMap,
} from "../../../../utils/specialEventGuests";
import { isGuestRegistrantType } from "./useRegistrantCatalogOptions";
import { editParticipantCatalogBridge } from "./editParticipantCatalogBridge";
import type { AppDispatch } from "../../../../redux/store";
import {
  clearPaymentDrafts,
  setAdditionalFeeInput,
  setApplyFirmCredit,
  setAmountRefunded,
  setCcEmail,
  setCcName,
  setCreditPayment,
  setGuests,
  setIsRegistrantModifyMode,
  setNewPaymentAmount,
  setNewPaymentDateMs,
  setNewPaymentMethod,
  setNewPaymentNote,
  setPersistedPaymentFields,
  setRefunded,
  setSelectedSpecialEventIds,
  setShowGuestValidationErrors,
  setSpecialEventGuestsMap,
  setRegistrationData,
} from "../../../../redux/states/editParticipant.state";
import {
  selectEditParticipantForm,
  selectEventData,
  selectEventId,
  selectFeeHighlights,
  selectNewPaymentDate,
  selectNewPaymentMethodOptions,
  selectPaymentMethodOptions,
  selectPayableBalance,
  selectPrepaidPaymentAmount,
  selectHasPrepaidPayment,
  selectRegistrantPaymentMethodDisabled,
  selectRegistrantFieldsDisabled,
  selectRegistrationData,
  selectSelectedSpecialEventIdSet,
  selectSpecialEventPrograms,
  selectStaffFormState,
  selectEditParticipantCatalog,
  selectOrganizationOptions,
  selectOrganizationLabel,
  selectOrganizationValue,
  selectRegistrantCatalogValue,
  selectPayments,
} from "../editParticipant.selectors";
import { useEditParticipantPatch } from "./useEditParticipantPatch";

export function useEditParticipantForm() {
  const dispatch = useDispatch<AppDispatch>();
  const { patchRegistration } = useEditParticipantPatch();

  const eventId = useSelector(selectEventId);
  const eventData = useSelector(selectEventData);
  const registrationData = useSelector(selectRegistrationData);
  const form = useSelector(selectEditParticipantForm);
  const staffFormState = useSelector(selectStaffFormState);

  const {
    isRegistrantModifyMode,
    guests,
    selectedSpecialEventIds,
    specialEventGuestsMap,
    additionalFeeInput,
    newPaymentAmount,
    newPaymentMethod,
    newPaymentNote,
    refunded,
    amountRefunded,
    applyFirmCredit,
    creditPayment,
    ccName,
    ccEmail,
    showGuestValidationErrors,
    persistedPaymentFields,
  } = form;

  const registrantFieldsDisabled = useSelector(selectRegistrantFieldsDisabled);
  const registrantPaymentMethodDisabled = useSelector(
    selectRegistrantPaymentMethodDisabled,
  );
  const payments = useSelector(selectPayments);
  const paymentFieldsDisabled = false;
  const paymentMethodOptions = useSelector(selectPaymentMethodOptions);
  const newPaymentMethodOptions = useSelector(selectNewPaymentMethodOptions);
  const payableBalance = useSelector(selectPayableBalance);
  const prepaidPaymentAmount = useSelector(selectPrepaidPaymentAmount);
  const hasPrepaidPayment = useSelector(selectHasPrepaidPayment);
  const specialEventPrograms = useSelector(selectSpecialEventPrograms);
  const selectedSpecialEventIdSet = useSelector(
    selectSelectedSpecialEventIdSet,
  );
  const feeHighlights = useSelector(selectFeeHighlights);
  const newPaymentDate = useSelector(selectNewPaymentDate);
  const catalog = useSelector(selectEditParticipantCatalog);
  const organizationOptions = useSelector(selectOrganizationOptions);
  const organizationLabel = useSelector(selectOrganizationLabel);
  const organizationValue = useSelector(selectOrganizationValue);
  const registrantCatalogValue = useSelector(selectRegistrantCatalogValue);

  // Keep drafted New Payment amount within Total Due when fees drop
  // (e.g. Additional Fees lowered from 500 → 250).
  // When balance hits 0, set "0" (not "") so Save can tell a cleared draft
  // from an untouched empty New Payment field, and skip auto prepaid create.
  useEffect(() => {
    const trimmed = (newPaymentAmount ?? "").trim();
    if (!trimmed) {
      return;
    }
    const parsed = parseFloat(trimmed);
    if (Number.isNaN(parsed)) {
      return;
    }
    const max = Math.max(0, payableBalance);
    if (parsed > max) {
      const next = String(max);
      dispatch(setNewPaymentAmount(next));
      if (max <= 0) {
        dispatch(setNewPaymentMethod(""));
      }
    }
  }, [dispatch, newPaymentAmount, payableBalance]);

  const registrantTypeOptions = useMemo(
    () =>
      Object.entries(attendeesTypeEnum)
        .filter(([key]) => key !== "guest")
        .map(([key, value]) => ({
          id: String(key),
          name: key === "speaker" ? "Guest Speaker" : value,
        })),
    [],
  );

  const handlePaymentStatusChange = useCallback(
    (paymentStatus: StatusType) => {
      patchRegistration({ paymentStatus });
    },
    [patchRegistration],
  );

  const handlePaymentMethodChange = useCallback(
    (paymentMethod: PaymentMethod) => {
      if (paymentMethod === "prepaid") {
        patchRegistration(applyStaffPrepaidSelection());
        return;
      }
      if (!hasPrepaidPaymentInLedger(payments)) {
        patchRegistration({
          paymentMethod,
          prepaid: false,
        });
        return;
      }
      patchRegistration({ paymentMethod });
    },
    [patchRegistration, payments],
  );

  const handleAttendeeTypeChange = useCallback(
    (option: { id: string; name: string } | null) => {
      if (!option || !registrationData) {
        return;
      }

      const nextType = option.id as AttendeeType;
      const wasGuestType = isGuestRegistrantType(registrationData.attendeeType);
      const isGuestType = isGuestRegistrantType(nextType);
      const wasStaff = registrationData.attendeeType === "staff";
      const isStaff = nextType === "staff";

      const patch: Partial<IRegistration> = { attendeeType: nextType };
      if (
        wasGuestType !== isGuestType ||
        (!wasGuestType && !isGuestType && wasStaff !== isStaff)
      ) {
        patch.firmId = undefined;
        patch.firmName = undefined;
        patch.personId = "";
        patch.personName = undefined;
        patch.personLastName = undefined;
        patch.personEmail = undefined;
        patch.badgeName = "";
      }

      const nextDraft = { ...registrationData, ...patch };
      const normalized = normalizeStaffPaymentFields(
        nextDraft,
        eventData,
        staffFormState,
      );
      patch.paymentMethod = normalized.paymentMethod;
      patch.paymentStatus = derivePaymentStatus(
        { ...nextDraft, paymentMethod: normalized.paymentMethod },
        eventData,
        staffFormState,
      );

      patchRegistration(patch);
    },
    [eventData, patchRegistration, registrationData, staffFormState],
  );

  const toggleSpecialEvent = useCallback(
    (programId: string, checked: boolean) => {
      const next = new Set(selectedSpecialEventIds);
      if (checked) {
        next.add(programId);
      }
      else {
        next.delete(programId);
      }
      dispatch(setSelectedSpecialEventIds(Array.from(next)));

      if (!checked && programId in specialEventGuestsMap) {
        const nextMap = { ...specialEventGuestsMap };
        delete nextMap[programId];
        dispatch(setSpecialEventGuestsMap(nextMap));
      }
    },
    [dispatch, selectedSpecialEventIds, specialEventGuestsMap],
  );

  const updateSpecialEventGuest = useCallback(
    (
      programId: string,
      guestIndex: number,
      guestPatch: Partial<IRegistrationGuest>,
    ) => {
      const list = [...(specialEventGuestsMap[programId] ?? [])];
      if (!list[guestIndex]) {
        return;
      }
      list[guestIndex] = { ...list[guestIndex], ...guestPatch };
      dispatch(
        setSpecialEventGuestsMap({
          ...specialEventGuestsMap,
          [programId]: list,
        }),
      );
    },
    [dispatch, specialEventGuestsMap],
  );

  const addSpecialEventGuest = useCallback(
    (programId: string) => {
      const list = specialEventGuestsMap[programId] ?? [];
      if (list.length >= MAX_REGISTRATION_GUESTS) {
        return;
      }
      dispatch(
        setSpecialEventGuestsMap({
          ...specialEventGuestsMap,
          [programId]: [
            ...list,
            createEmptyRegistrationGuest(registrationData?.registrationDate),
          ],
        }),
      );
    },
    [dispatch, registrationData?.registrationDate, specialEventGuestsMap],
  );

  const removeSpecialEventGuest = useCallback(
    (programId: string, guestIndex: number) => {
      const list = specialEventGuestsMap[programId] ?? [];
      const nextList = list.filter((_, i) => i !== guestIndex);
      const next: SpecialEventGuestsMap = { ...specialEventGuestsMap };
      if (nextList.length > 0) {
        next[programId] = nextList;
      }
      else {
        delete next[programId];
      }
      dispatch(setSpecialEventGuestsMap(next));
    },
    [dispatch, specialEventGuestsMap],
  );

  const copyMainGuestsToSpecialEvent = useCallback(
    (programId: string) => {
      const mainGuests = sanitizeGuestsForPersist(
        guests,
        registrationData?.registrationDate,
      );
      if (mainGuests.length === 0) {
        return;
      }
      dispatch(
        setSpecialEventGuestsMap({
          ...specialEventGuestsMap,
          [programId]: mainGuests.map((guest) => ({
            ...guest,
            id: crypto.randomUUID(),
          })),
        }),
      );
    },
    [
      dispatch,
      guests,
      registrationData?.registrationDate,
      specialEventGuestsMap,
    ],
  );

  const prepareRegistrationPayload = useCallback(
    (
      scope: "full" | "registrant",
      getActorLabel: () => string,
      loggedPersonId?: string,
    ): IRegistration | null => {
      if (!registrationData || !eventId) {
        return null;
      }

      const guestsForSave = applyGuestModificationDates(
        sanitizeGuestsForPersist(guests, registrationData.registrationDate),
        normalizeRegistrationGuests(registrationData),
      );
      const specialEventGuestsForSave = applySpecialEventGuestModificationDates(
        sanitizeSpecialEventGuestsForPersist(
          specialEventGuestsMap,
          selectedSpecialEventIds,
          registrationData.registrationDate,
        ),
        normalizeSpecialEventGuestsMap(registrationData),
      );
      const specialEventGuestNamesForSave = deriveSpecialEventGuestNames(
        specialEventGuestsForSave,
      );

      if (scope === "registrant") {
        const persisted = persistedPaymentFields;
        const additionalFee =
          typeof persisted.additionalFee === "number" &&
          !Number.isNaN(persisted.additionalFee)
            ? persisted.additionalFee
            : 0;
        const draft: IRegistration = {
          ...registrationData,
          eventId,
          guests: guestsForSave,
          guestName: undefined,
          guestName2: undefined,
          badgeName: registrationData.badgeName ?? "",
          specialRequests:
            registrationData.specialRequests?.trim() || undefined,
          note: registrationData.note?.trim() || undefined,
          additionalFee,
          amountPaid:
            typeof persisted.amountPaid === "number" &&
            !Number.isNaN(persisted.amountPaid)
              ? persisted.amountPaid
              : 0,
          paymentMethod: persisted.paymentMethod,
          paymentStatus:
            persisted.paymentStatus ?? registrationData.paymentStatus,
          selectedSpecialEventIds:
            selectedSpecialEventIds.length > 0
              ? selectedSpecialEventIds
              : undefined,
          specialEventGuests:
            Object.keys(specialEventGuestsForSave).length > 0
              ? specialEventGuestsForSave
              : undefined,
          specialEventGuestNames: specialEventGuestNamesForSave,
        };

        applyStaffParticipantPaymentStatus(
          draft,
          eventData ?? null,
          staffFormState,
        );
        const normalized = normalizeStaffPaymentFields(
          draft,
          eventData ?? null,
          staffFormState,
        );
        draft.paymentMethod = normalized.paymentMethod ?? draft.paymentMethod;
        draft.paymentStatus = normalized.paymentStatus ?? draft.paymentStatus;

        (draft as any).guestActionBy = getActorLabel();
        (draft as any).guestActionById = loggedPersonId?.trim() || null;

        return draft;
      }

      const additionalFee = parseAdditionalFee(additionalFeeInput);
      const currentPaid =
        typeof registrationData.amountPaid === "number" &&
        !Number.isNaN(registrationData.amountPaid)
          ? registrationData.amountPaid
          : 0;
      const registrantPaymentMethod =
        registrationData.paymentMethod as PaymentMethod;
      const isPrepaidMethod = registrantPaymentMethod === "prepaid";
      // New Payment drafts apply only when staff picks a method in that section.
      const paymentIncrement = isPrepaidMethod
        ? null
        : resolveStaffPaymentIncrement({
            paymentMethod: newPaymentMethod || "",
            typedAmount: newPaymentAmount,
            registration: registrationData,
            event: eventData,
          });
      const nextAmountPaid =
        paymentIncrement != null
          ? Math.round((currentPaid + paymentIncrement) * 100) / 100
          : currentPaid;

      const draft: IRegistration = {
        ...registrationData,
        eventId,
        guests: guestsForSave,
        guestName: undefined,
        guestName2: undefined,
        badgeName: registrationData.badgeName ?? "",
        specialRequests: registrationData.specialRequests?.trim() || undefined,
        note: registrationData.note?.trim() || undefined,
        additionalFee,
        amountPaid: nextAmountPaid,
        paymentMethod: registrantPaymentMethod,
        prepaid: isPrepaidMethod ? true : registrationData.prepaid,
        selectedSpecialEventIds:
          selectedSpecialEventIds.length > 0
            ? selectedSpecialEventIds
            : undefined,
        specialEventGuests:
          Object.keys(specialEventGuestsForSave).length > 0
            ? specialEventGuestsForSave
            : undefined,
        specialEventGuestNames: specialEventGuestNamesForSave,
      };

      applyStaffParticipantPaymentStatus(
        draft,
        eventData ?? null,
        staffFormState,
      );
      const normalized = normalizeStaffPaymentFields(
        draft,
        eventData ?? null,
        staffFormState,
      );
      draft.paymentMethod = normalized.paymentMethod ?? draft.paymentMethod;
      draft.paymentStatus = normalized.paymentStatus ?? draft.paymentStatus;

      (draft as any).staffPersistAmountPaid = true;
      (draft as any).staffAdjustPrepaid = true;
      (draft as any).staffBypassPrepaidOfficeSlot = true;
      (draft as any).guestActionBy = getActorLabel();
      (draft as any).guestActionById = loggedPersonId?.trim() || null;
      if (newPaymentNote.trim()) {
        (draft as any).amountPaidHistoryNote = newPaymentNote.trim();
      }

      return draft;
    },
    [
      additionalFeeInput,
      eventData,
      eventId,
      guests,
      newPaymentAmount,
      newPaymentMethod,
      newPaymentNote,
      persistedPaymentFields,
      registrationData,
      selectedSpecialEventIds,
      specialEventGuestsMap,
      staffFormState,
    ],
  );

  const syncFormFromRegistration = useCallback(
    (registration: IRegistration) => {
      dispatch(setGuests(normalizeRegistrationGuests(registration)));
      dispatch(
        setSelectedSpecialEventIds(
          Array.isArray(registration.selectedSpecialEventIds)
            ? registration.selectedSpecialEventIds.filter(Boolean)
            : [],
        ),
      );
      dispatch(
        setSpecialEventGuestsMap(normalizeSpecialEventGuestsMap(registration)),
      );
      dispatch(
        setAdditionalFeeInput(
          registration.additionalFee != null
            ? String(registration.additionalFee)
            : "",
        ),
      );
      dispatch(
        setPersistedPaymentFields({
          amountPaid: registration.amountPaid,
          additionalFee: registration.additionalFee,
          paymentMethod: registration.paymentMethod,
          paymentStatus: registration.paymentStatus,
        }),
      );
    },
    [dispatch],
  );

  const validateGuestFields = useCallback((): string | null => {
    if (hasVisibleGuestWithMissingName(guests)) {
      return "Guest name is required. Fill in the name or remove the guest.";
    }
    if (
      hasSpecialEventGuestsWithMissingNames(
        specialEventGuestsMap,
        selectedSpecialEventIds,
      )
    ) {
      return "Special event guest name is required. Fill in the name or remove the guest.";
    }
    return null;
  }, [guests, selectedSpecialEventIds, specialEventGuestsMap]);

  const handleOrganizationChange =
    editParticipantCatalogBridge.handlers?.handleOrganizationChange ??
    (() => undefined);
  const handleRegistrantChange =
    editParticipantCatalogBridge.handlers?.handleRegistrantChange ??
    (() => undefined);

  return {
    isRegistrantModifyMode,
    setIsRegistrantModifyMode: (value: boolean) =>
      dispatch(setIsRegistrantModifyMode(value)),
    guests,
    setGuests: (value: SetStateAction<IRegistrationGuest[]>) => {
      const next = typeof value === "function" ? value(guests) : value;
      dispatch(setGuests(next));
    },
    selectedSpecialEventIds,
    specialEventGuestsMap,
    additionalFeeInput,
    setAdditionalFeeInput: (value: string) =>
      dispatch(setAdditionalFeeInput(value)),
    newPaymentDate,
    setNewPaymentDate: (date: Date | null) =>
      dispatch(setNewPaymentDateMs(date ? date.getTime() : null)),
    newPaymentAmount,
    setNewPaymentAmount: (value: string) =>
      dispatch(setNewPaymentAmount(value)),
    newPaymentMethod,
    setNewPaymentMethod: (value: PaymentMethod | "") => {
      dispatch(setNewPaymentMethod(value));
      // Suggested prepaid amount; staff can still type any value.
      if (
        value === "prepaid" &&
        registrationData &&
        (newPaymentAmount ?? "").trim() === ""
      ) {
        const fee = getPrepaidPaymentAmount(eventData, registrationData);
        if (fee > 0) {
          const capped = Math.min(fee, Math.max(0, payableBalance));
          dispatch(setNewPaymentAmount(String(capped)));
        }
      }
    },
    prepaidPaymentAmount,
    hasPrepaidPayment,
    newPaymentNote,
    setNewPaymentNote: (value: string) => dispatch(setNewPaymentNote(value)),
    refunded,
    setRefunded: (value: boolean) => dispatch(setRefunded(value)),
    amountRefunded,
    setAmountRefunded: (value: string) => dispatch(setAmountRefunded(value)),
    applyFirmCredit,
    setApplyFirmCredit: (value: boolean) => dispatch(setApplyFirmCredit(value)),
    creditPayment,
    setCreditPayment: (value: string) => dispatch(setCreditPayment(value)),
    ccName,
    setCcName: (value: string) => dispatch(setCcName(value)),
    ccEmail,
    setCcEmail: (value: string) => dispatch(setCcEmail(value)),
    registrantFieldsDisabled,
    registrantPaymentMethodDisabled,
    paymentFieldsDisabled,
    registrantTypeOptions,
    paymentMethodOptions,
    newPaymentMethodOptions,
    payableBalance,
    specialEventPrograms,
    selectedSpecialEventIdSet,
    handlePaymentStatusChange,
    handlePaymentMethodChange,
    handleAttendeeTypeChange,
    toggleSpecialEvent,
    updateSpecialEventGuest,
    addSpecialEventGuest,
    removeSpecialEventGuest,
    copyMainGuestsToSpecialEvent,
    prepareRegistrationPayload,
    syncFormFromRegistration,
    clearPaymentDrafts: () => dispatch(clearPaymentDrafts()),
    feeHighlights,
    showGuestValidationErrors,
    setShowGuestValidationErrors: (value: boolean) =>
      dispatch(setShowGuestValidationErrors(value)),
    validateGuestFields,
    organizationLabel,
    organizationOptions,
    organizationValue,
    registrantCatalogOptions: catalog.registrantOptions,
    registrantCatalogValue,
    handleOrganizationChange,
    handleRegistrantChange,
    patchRegistration,
  };
}
